'use client';

import { useCallback, useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { useSpatialPorts } from '../data/ports';
import { SignInRequired } from '../data/savedDesigns';
import {
  addPostcodes,
  canEditAreas,
  COVERAGE_SAVED_EVENT,
  coverageByArea,
  NotAFitter,
  tradeLabel,
  type FitterAccount,
  type TradeJobs,
} from '../data/tradeJobs';
import { headingLevel, SkLink, text, type HeadingLevel, type Localized } from '../shared/primitives';
import { NotOpen } from '../shared/NotOpen';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { FITTER_COVERAGE_CSS, TRADE_SHARED_CSS } from '../trade/tradeCss';
import {
  anchorId,
  blockerText,
  FITTER_APPLICATION_HREF,
  messageOf,
  sendToSignIn,
  tradeOptions,
  type TradeOptionProp,
} from '../trade/tradeShared';
import { homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * Where a fitter works (site mockup `trade.html`, "Your coverage"): the signed-in
 * fitter's standing with the shop, their postcode areas and districts as
 * removable chips, their trades, and a summary of the coverage by area.
 *
 * <h3>What the fitter can change</h3>
 * Only their areas. Trades are shown read-only: the shop approves each trade
 * against a registration (gas, electrical), so only the shop changes them.
 * Areas can be changed once there is a fitter account (ACTIVE or SUSPENDED); an
 * application under review, or refused, shows them read-only.
 *
 * <h3>Standing</h3>
 * APPLIED is "under review", REJECTED "not approved", SUSPENDED and any
 * blockers (agreement out of date, insurance not current) are listed, with a
 * button to the fitter's documents page when the shop sent a link for it.
 *
 * <h3>Visitors who are not fitters</h3>
 * Nobody signed in: sent to sign in and back (the account pages' convention).
 * Signed in but not a fitter: a link to the fitter application page.
 *
 * <h3>Postcodes</h3>
 * Areas ("BS") and districts ("BS16") only; a full postcode is refused with
 * "Just the first part, e.g. BS16". Several can be typed or pasted at once.
 * Rules: `checkPostcode` in `data/tradeJobs.ts`; the server checks them again.
 *
 * <h3>The "map"</h3>
 * The coverage is shown as a grid of areas, each with its Royal Mail name and
 * either "Whole area" or its districts; drawing real postcode geography needs
 * boundary data the theme does not have.
 */
export interface SpatialKitchenFitterCoverageProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  /** Names for trade ids, e.g. `KITCHEN_FITTING` → "Kitchen fitting". Empty uses the built-in names. */
  trades?: TradeOptionProp[];
  saveLabel?: Localized;
  /** Where a visitor who is not signed in is sent (and back). */
  signInHref?: string;
  /** The fitter application page, linked for a signed-in visitor who is not a fitter. */
  applyHref?: string;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
  /** The section's id, for links such as `#coverage` or `#jobs`. */
  anchorId?: string;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'signIn' }
  | { kind: 'notFitter' }
  | { kind: 'ready'; account: FitterAccount };
type Status = { kind: 'ok' | 'error'; message: string } | null;

function joinCodes(codes: string[]): string {
  if (codes.length <= 1) return codes.join('');
  return `${codes.slice(0, -1).join(', ')} and ${codes[codes.length - 1]}`;
}

export function SpatialKitchenFitterCoverage(props: SpatialKitchenFitterCoverageProps) {
  const { tradeJobs } = useSpatialPorts();
  if (!tradeJobs) {
    return (
      <NotOpen
        level={props.headingLevel ?? 'h2'}
        title={text(props.title, 'Choose where you work')}
        message="Fitter accounts are not open online yet. Apply to become a fitter and tell us the postcodes you cover."
        contactHref={props.applyHref?.trim() || FITTER_APPLICATION_HREF}
      />
    );
  }
  return <FitterCoverage {...props} tradeJobs={tradeJobs} />;
}

/** One sentence on where the fitter stands. */
export function standingText(account: Pick<FitterAccount, 'state' | 'canQuote' | 'businessName'>): string {
  const name = account.businessName ? ` for ${account.businessName}` : '';
  switch (account.state) {
    case 'APPLIED':
      return 'Your fitter application is under review. We will email you when it has been decided.';
    case 'REJECTED':
      return 'Your fitter application was not approved. Please contact us if you would like to talk about it.';
    case 'SUSPENDED':
      return `Your fitter account${name} is suspended, so you cannot quote for jobs.`;
    default:
      return account.canQuote
        ? `Your fitter account${name} is active. Jobs in your areas and trades are listed below.`
        : `Your fitter account${name} is active, but you cannot quote until this is sorted:`;
  }
}

function FitterCoverage(props: SpatialKitchenFitterCoverageProps & { tradeJobs: TradeJobs }) {
  const id = useId();
  const { tradeJobs } = props;
  const Title = headingLevel(props.headingLevel, 'h2');
  const names = tradeOptions(props.trades);
  const inputRef = useRef<HTMLInputElement>(null);
  const applyHref = props.applyHref?.trim() || FITTER_APPLICATION_HREF;

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [postcodes, setPostcodes] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [fieldNote, setFieldNote] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const signInHref = props.signInHref;
  const fetchAccount = useCallback(() => {
    let current = true;
    setLoad({ kind: 'loading' });
    tradeJobs
      .getAccount()
      .then((account) => {
        if (!current) return;
        setPostcodes(account.postcodes);
        setDirty(false);
        setLoad({ kind: 'ready', account });
      })
      .catch((error: unknown) => {
        if (!current) return;
        if (error instanceof SignInRequired) {
          setLoad({ kind: 'signIn' });
          sendToSignIn(signInHref);
        } else if (error instanceof NotAFitter) {
          setLoad({ kind: 'notFitter' });
        } else {
          setLoad({ kind: 'error', message: messageOf(error, 'Your fitter account could not be loaded.') });
        }
      });
    return () => {
      current = false;
    };
  }, [tradeJobs, signInHref]);

  useEffect(() => fetchAccount(), [fetchAccount]);

  const account = load.kind === 'ready' ? load.account : null;
  const editable = account ? canEditAreas(account) : false;

  /** Adds what was typed; returns the postcodes after it, or `null` when something could not be read. */
  function commit(typed: string): string[] | null {
    if (!typed.trim()) {
      setFieldError(null);
      return postcodes;
    }
    const result = addPostcodes(postcodes, typed);
    const notes: string[] = [];
    if (result.added.length) notes.push(`Added ${joinCodes(result.added)}.`);
    if (result.skipped.length) notes.push(`${joinCodes(result.skipped)} ${result.skipped.length === 1 ? 'is' : 'are'} already covered.`);
    if (result.absorbed.length) notes.push(`${joinCodes(result.absorbed)} ${result.absorbed.length === 1 ? 'is' : 'are'} now part of a whole area.`);
    setFieldNote(notes.length ? notes.join(' ') : null);
    setFieldError(result.error ?? null);
    setDraft(result.rejected.join(', '));
    if (result.added.length || result.absorbed.length) {
      setPostcodes(result.postcodes);
      setDirty(true);
      setStatus(null);
    }
    return result.error ? null : result.postcodes;
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit(draft);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text');
    if (!/[\s,;]/.test(pasted.trim())) return;
    event.preventDefault();
    commit(`${draft} ${pasted}`);
  }

  function remove(code: string) {
    setPostcodes((list) => list.filter((p) => p !== code));
    setDirty(true);
    setStatus(null);
    setFieldNote(`Removed ${code}.`);
    inputRef.current?.focus();
  }

  async function save() {
    const next = commit(draft);
    if (!next) return;
    if (next.length === 0) {
      setFieldError('Add at least one postcode area or district.');
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const saved = await tradeJobs.saveAreas(next);
      setPostcodes(saved.postcodes);
      setDirty(false);
      setLoad({ kind: 'ready', account: saved });
      setStatus({ kind: 'ok', message: 'Your areas are saved.' });
      window.dispatchEvent(new CustomEvent(COVERAGE_SAVED_EVENT, { detail: saved }));
    } catch (error) {
      if (error instanceof SignInRequired) {
        sendToSignIn(signInHref);
        return;
      }
      setStatus({ kind: 'error', message: messageOf(error, 'Your areas were not saved. Please try again.') });
    } finally {
      setBusy(false);
    }
  }

  const areas = coverageByArea(postcodes);
  const describedBy = [`${id}-hint`, fieldError ? `${id}-error` : ''].filter(Boolean).join(' ');
  const intro = text(props.intro, 'Add postcode districts, or a whole area such as BS. Only jobs inside them are shown to you.');
  const blockers = account ? account.blockers.filter((b) => !(b === 'SUSPENDED' && account.state === 'SUSPENDED')) : [];

  return (
    <BlockRoot as="section" id={anchorId(props.anchorId)} className={`sk sk-trade sk-fcov ${homeSurfaceClass(props.surface, 'white')}`} aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="trade" css={TRADE_SHARED_CSS} />
      <SkStyles id="fitter-coverage" css={FITTER_COVERAGE_CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          <p className="sk-eyebrow">{text(props.eyebrow, 'Your coverage')}</p>
          <Title id={`${id}-title`} className="sk-heading sk-h2">
            {text(props.title, 'Choose where you work')}
          </Title>
          {intro && <p className="sk-lead">{intro}</p>}
        </div>

        {load.kind === 'error' && (
          <div role="alert">
            <p className="sk-trade__status sk-trade__status--error">{load.message}</p>
            <div className="sk-trade__actions">
              <button type="button" className="sk-trade__retry" onClick={() => fetchAccount()}>
                Try again
              </button>
            </div>
          </div>
        )}

        {load.kind === 'signIn' && (
          <div className="sk-trade__panel">
            <p className="sk-body">
              Sign in to see your fitter account. <SkLink href={props.signInHref?.trim() || '/sign-in'}>Sign in</SkLink>
            </p>
          </div>
        )}

        {load.kind === 'notFitter' && (
          <div className="sk-trade__panel">
            <p className="sk-body">You do not have a fitter account yet. Apply to become a fitter, and once the shop approves you, jobs in your areas appear here.</p>
            <div className="sk-trade__actions">
              <SkLink href={applyHref} className="sk-btn sk-btn--primary">
                Apply to become a fitter
              </SkLink>
            </div>
          </div>
        )}

        {account && (
          <div className="sk-trade__panel sk-fcov__standing" role="status">
            <p className="sk-body">
              <strong>{standingText(account)}</strong>
            </p>
            {blockers.length > 0 && (
              <ul className="sk-fcov__blockers">
                {blockers.map((blocker) => (
                  <li key={blocker}>{blockerText(blocker)}</li>
                ))}
              </ul>
            )}
            {account.documentsLink && (
              <div className="sk-trade__actions">
                <SkLink href={account.documentsLink} className={`sk-btn ${blockers.length ? 'sk-btn--primary' : 'sk-btn--secondary'}`}>
                  {blockers.length ? 'Update your documents' : 'Your documents'}
                </SkLink>
              </div>
            )}
          </div>
        )}

        {(load.kind === 'loading' || account) && (
          <div className="sk-fcov__grid">
            <fieldset className="sk-trade__panel sk-fcov__form" disabled={load.kind === 'loading' || !editable} aria-busy={load.kind === 'loading'}>
              <legend className="sk-visually-hidden">Your coverage</legend>
              <div className="sk-trade__field">
                {editable || load.kind === 'loading' ? (
                  <label className="sk-trade__label" htmlFor={`${id}-pc`}>
                    Postcode areas and districts
                  </label>
                ) : (
                  <p className="sk-trade__label">Postcode areas and districts</p>
                )}
                <div className="sk-fcov__box" data-invalid={fieldError ? '' : undefined} onClick={() => inputRef.current?.focus()}>
                  {postcodes.length > 0 && (
                    <ul className="sk-fcov__chips" aria-label="Postcodes you cover">
                      {postcodes.map((code) => (
                        <li key={code} className="sk-fcov__chip">
                          {code}
                          {editable && (
                            <button type="button" aria-label={`Remove ${code}`} onClick={() => remove(code)}>
                              ×
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {(editable || load.kind === 'loading') && (
                    <>
                      <input
                        ref={inputRef}
                        id={`${id}-pc`}
                        className="sk-fcov__input"
                        value={draft}
                        placeholder="e.g. BS16, then Enter"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        enterKeyHint="done"
                        aria-invalid={fieldError ? true : undefined}
                        aria-describedby={describedBy}
                        onChange={(e) => {
                          setDraft(e.target.value);
                          if (fieldError) setFieldError(null);
                        }}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                      />
                      <button type="button" className="sk-trade__mini" onClick={() => commit(draft)} disabled={!draft.trim()}>
                        Add
                      </button>
                    </>
                  )}
                </div>
                <p className="sk-trade__hint" id={`${id}-hint`}>
                  {editable || load.kind === 'loading'
                    ? 'Add a whole area (e.g. BS) to cover every district in it. Separate several with commas.'
                    : 'You can change your areas once the shop has approved your fitter account.'}
                </p>
                {fieldError && (
                  <p className="sk-trade__error" id={`${id}-error`}>
                    {fieldError}
                  </p>
                )}
                <p className="sk-trade__hint" role="status" aria-live="polite">
                  {fieldNote ?? ''}
                </p>
              </div>

              <div className="sk-trade__field">
                <p className="sk-trade__label" id={`${id}-trades`}>
                  Your trades
                </p>
                {account && account.trades.length > 0 ? (
                  <ul className="sk-fcov__tradelist" aria-labelledby={`${id}-trades`}>
                    {account.trades.map((trade) => (
                      <li key={trade}>{tradeLabel(trade, names)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="sk-fcov__empty">{account ? 'No trades yet.' : 'Loading…'}</p>
                )}
                <p className="sk-trade__hint">Your trades are changed by the shop, not here. Contact us to add or remove one.</p>
              </div>

              {editable && (
                <div className="sk-trade__actions">
                  <button type="button" className="sk-btn sk-btn--primary" disabled={busy || (!dirty && !draft.trim())} onClick={save}>
                    {busy ? 'Saving…' : text(props.saveLabel, 'Save areas')}
                  </button>
                  {dirty && !busy && <span className="sk-trade__hint">Unsaved changes</span>}
                </div>
              )}
              {status && (
                <p
                  className={`sk-trade__status sk-trade__status--${status.kind}`}
                  role={status.kind === 'error' ? 'alert' : 'status'}
                >
                  {status.message}
                </p>
              )}
            </fieldset>

            <div className="sk-fcov__map">
              <h3>Where you cover</h3>
              {areas.length ? (
                <ul className="sk-fcov__areas">
                  {areas.map((area) => (
                    <li key={area.area} className="sk-fcov__area">
                      <strong>{area.area}</strong>
                      <span>{area.name}</span>
                      <span>
                        {area.whole
                          ? 'Whole area'
                          : `${area.districts.length} district${area.districts.length === 1 ? '' : 's'}: ${area.districts.join(', ')}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sk-fcov__empty">
                  {load.kind === 'loading' ? 'Loading…' : 'No postcodes yet. Add the districts you work in to see jobs there.'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </BlockRoot>
  );
}
